import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/supabase';

const DEFAULT_PRICING_TIERS = {
  residential_basic: [
    { minGb: 1, maxGb: 4, pricePerGb: 0.80, range: '1-4GB' },
    { minGb: 5, maxGb: 9, pricePerGb: 0.80, range: '5-9GB' },
    { minGb: 10, maxGb: 49, pricePerGb: 0.80, range: '10-49GB' },
    { minGb: 50, maxGb: 99, pricePerGb: 0.80, range: '50-99GB' },
    { minGb: 100, maxGb: 249, pricePerGb: 0.80, range: '100-249GB' },
    { minGb: 250, maxGb: 499, pricePerGb: 0.80, range: '250-499GB' },
    { minGb: 500, maxGb: 999, pricePerGb: 0.80, range: '500-999GB' },
    { minGb: 1000, maxGb: 2499, pricePerGb: 0.80, range: '1,000-2,499GB' },
    { minGb: 2500, maxGb: 4999, pricePerGb: 0.59, range: '2,500-4,999GB' },
    { minGb: 5000, maxGb: 9999, pricePerGb: 0.49, range: '5,000-9,999GB' },
    { minGb: 10000, maxGb: null, pricePerGb: 0.40, range: '10,000GB+' },
  ],
  residential_premium: [
    { minGb: 1, maxGb: 4, pricePerGb: 1.61, range: '1-4GB' },
    { minGb: 5, maxGb: 9, pricePerGb: 1.61, range: '5-9GB' },
    { minGb: 10, maxGb: 49, pricePerGb: 1.61, range: '10-49GB' },
    { minGb: 50, maxGb: 99, pricePerGb: 1.61, range: '50-99GB' },
    { minGb: 100, maxGb: 249, pricePerGb: 1.61, range: '100-249GB' },
    { minGb: 250, maxGb: 499, pricePerGb: 1.61, range: '250-499GB' },
    { minGb: 500, maxGb: 999, pricePerGb: 1.61, range: '500-999GB' },
    { minGb: 1000, maxGb: 2499, pricePerGb: 1.59, range: '1,000-2,499GB' },
    { minGb: 2500, maxGb: 4999, pricePerGb: 1.19, range: '2,500-4,999GB' },
    { minGb: 5000, maxGb: 9999, pricePerGb: 0.98, range: '5,000-9,999GB' },
    { minGb: 10000, maxGb: null, pricePerGb: 0.89, range: '10,000GB+' },
  ],
};

export const usePricing = (proxyType = null) => {
  const [tiers, setTiers] = useState([]);
  const [allTiers, setAllTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPricing = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await db.pricingTiers.getAll();

      if (error) throw error;

      const grouped = {};

      (data || []).forEach(tier => {
        if (tier.is_payg) return;
        if (!grouped[tier.proxy_type]) {
          grouped[tier.proxy_type] = [];
        }
        grouped[tier.proxy_type].push({
          minGb: parseFloat(tier.min_gb),
          maxGb: tier.max_gb ? parseFloat(tier.max_gb) : null,
          pricePerGb: parseFloat(tier.price_per_gb),
          range: tier.max_gb
            ? `${tier.min_gb}-${tier.max_gb}GB`
            : `${tier.min_gb}GB+`,
        });
      });

      const finalTiers = {
        ...grouped,
        residential_basic: DEFAULT_PRICING_TIERS.residential_basic,
        residential_premium: DEFAULT_PRICING_TIERS.residential_premium,
      };

      setAllTiers(finalTiers);

      if (proxyType && finalTiers[proxyType]) {
        setTiers(finalTiers[proxyType]);
      }
    } catch (err) {
      setAllTiers(DEFAULT_PRICING_TIERS);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [proxyType]);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const calculatePrice = useCallback((type, gbAmount) => {
    const typeTiers = allTiers[type] || [];

    const tier = typeTiers.find(t =>
      gbAmount >= t.minGb && (t.maxGb === null || gbAmount <= t.maxGb)
    );

    if (!tier) {
      const highestTier = typeTiers[typeTiers.length - 1];
      if (highestTier) {
        return {
          totalPrice: gbAmount * highestTier.pricePerGb,
          pricePerGb: highestTier.pricePerGb,
          tier: highestTier,
        };
      }
      return { totalPrice: 0, pricePerGb: 0, tier: null };
    }

    return {
      totalPrice: gbAmount * tier.pricePerGb,
      pricePerGb: tier.pricePerGb,
      tier,
    };
  }, [allTiers]);

  const getTiersForType = (type) => {
    return allTiers[type] || [];
  };

  const formatPrice = (price, decimals = 2) => {
    return `$${parseFloat(price).toFixed(decimals)}`;
  };

  return {
    tiers,
    allTiers,
    loading,
    error,
    calculatePrice,
    getTiersForType,
    formatPrice,
    refresh: fetchPricing,
  };
};

export default usePricing;
